import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { appConfig, appInit } from '../config'
import { changeLanguage, setRedirectedFromWTR, setSaveWtrFundsRedirection } from '../redux/slices/app'
import { useAppState, useUserApplications } from './hooks'
import appRoutes from './routes'
import { WtrPostMessageType } from './types'
import { useEmailRedirect } from './useEmailRedirect'
import Utils from './utils'

// THPRT-6271: extracted from the retired utils/useRedirect.ts — the listener was
// its only export still consumed (by App) after the legacy step machine deletion.
export const useWtrPostMessageListener = () => {
    const { uncompletedApplication, wtrFundsRoute } = useAppState()
    const history = useHistory()
    const dispatch = useDispatch()
    const validateEmail = useEmailRedirect()
    const { incompleteApplication, hasApprovedCFD } = useUserApplications()

    const resumeApplication = useCallback(() => {
        if (uncompletedApplication) {
            if (uncompletedApplication.completed) return
            // THPRT-6269: every application resumes in the General/Simplified onboarding flow
            history.push(appRoutes.onboarding)
        }
        else
            history.push(appRoutes.account.verify.root)
    }, [history, uncompletedApplication])

    const redirectFundsMessage = (event: WtrPostMessageType) => {
        const lastVisited = wtrFundsRoute
        const fundsLocation = '/dashboard/funds/'
        switch (lastVisited) {
            case `${fundsLocation}deposit`:
                history.push(`${appRoutes.dashboard.funds.deposit}?account=${event.data.accountId}`)
                break
            case `${fundsLocation}withdraw`:
                history.push(`${appRoutes.dashboard.funds.withdraw}?account=${event.data.accountId}`)
                break
            case `${fundsLocation}transfer`:
                history.push(appRoutes.dashboard.funds.transfer)
                break
            case `${fundsLocation}history`:
                history.push(`${appRoutes.dashboard.funds.history}?account=${event.data.accountId}`)
                break
            case `${fundsLocation}accounts`:
                history.push(appRoutes.dashboard.funds.bankAccounts)
                break
            default:
                history.push(`${appRoutes.dashboard.funds.deposit}?account=${event.data.accountId}`)
                break
        }
    }

    const wtrPostMessageListener = async (event: WtrPostMessageType) => {
        await appInit
        const testWTRDomains = [
            appConfig.WTR_URL,
            "https://web-preprod.thinktrader.com",
            "https://web-staging.thinktrader.com",
            "https://development.d3eqw3u4656f3b.amplifyapp.com"
        ]
        const prodWTRDomains = [
            appConfig.WTR_URL,
            "https://web-preprod.thinktrader.com",
        ]
        const correctDomain = Utils.isTestEnv()
            ? testWTRDomains.includes(event.origin)
            : prodWTRDomains.includes(event.origin)
        if (correctDomain) {
            if (Utils.isTestEnv()) console.log('>>> message from WTR', event.data)
            dispatch(setRedirectedFromWTR(true))
            dispatch(setSaveWtrFundsRedirection(false))

            if (event.data.lang) {
                dispatch(changeLanguage(event.data.lang))
            }

            switch (event.data.message) {
                case 'funds':
                    dispatch(setSaveWtrFundsRedirection(true))
                    redirectFundsMessage(event)
                    break
                case 'deposit':
                    dispatch(setSaveWtrFundsRedirection(true))
                    history.push(`${appRoutes.dashboard.funds.deposit}?account=${event.data.accountId}`)
                    break
                case 'reports':
                    history.push(`${appRoutes.dashboard.funds.tradeSummary}?account=${event.data.accountId}`)
                    break
                case 'openLive':
                    if (!await validateEmail()) break

                    if (incompleteApplication) {
                        resumeApplication()
                    } else if (hasApprovedCFD) {
                        history.push(appRoutes.dashboard.accounts.createLive)
                    } else {
                        const code = event.data.countryCode3
                        // Guard the WTR-supplied code to a 3-letter ISO shape before
                        // putting it in the URL query.
                        if (code && /^[A-Z]{3}$/.test(code))
                            history.push(`${appRoutes.account.createAccount.live}?country=${code}`)
                        else history.push(appRoutes.account.createAccount.live)
                    }
                    break
                case 'openDemo':
                    history.push(appRoutes.dashboard.accounts.createDemo)
                    break
                case 'uploadDocuments':
                    history.push(appRoutes.account.verify.root)
                    break
                case 'completeOnboarding':
                    history.push(appRoutes.account.simplified.accountCreated)
                    break
                case 'status':
                    history.push(`${appRoutes.dashboard.funds.status}?status=${event.data.status}&transaction_id=${event.data.transactionId}`)
                    break
            }
        }
    }

    return wtrPostMessageListener
}
